// src/pages/BulletinsPrix.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
} from '@mui/material';
import moment from 'moment';
import Footer from '../navbar/Footer';
import { getPublications } from '../services/publicationService';

function BulletinsPrix() {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [bulletinsData, setBulletinsData] = useState([]);
  const [filteredBulletins, setFilteredBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'bmp', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            // datePublication est utilisé pour le filtre et l'affichage (correspond à publication_date)
            datePublication: new Date(item.publication_date),
            // date peut être utilisé pour autre chose (période couverte) mais non utilisé dans l'affichage
            date: item.date ? new Date(item.date) : null,
          }));
          // Trier par date de publication décroissante (plus récent d'abord)
          const sorted = mapped.sort((a, b) => b.datePublication - a.datePublication);
          setBulletinsData(sorted);
          setFilteredBulletins(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);

    let filtered;
    if (year === 'Tous') {
      filtered = [...bulletinsData];
    } else {
      filtered = bulletinsData.filter((bulletin) => {
        const bulletinYear = bulletin.datePublication.getFullYear();
        return bulletinYear === parseInt(year);
      });
    }
    // Trier par date de publication décroissante
    filtered.sort((a, b) => b.datePublication - a.datePublication);
    setFilteredBulletins(filtered);
    setVisibleCount(year === 'Tous' ? 4 : 2);
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      secondary: { main: '#f50057' },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 'bold',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.1)',
            },
          },
        },
      },
    },
  });

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = bulletinsData.map((bulletin) => bulletin.datePublication.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bulletinsData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '60px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
              Les Bulletins Mensuels des Prix
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '60px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
              Les Bulletins Mensuels des Prix
            </Typography>
            <Divider style={{ margin: '20px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: '#f0f4f8', paddingBottom: '20px' }}>
        <NavigationBar />
        <Container maxWidth="lg" style={{ paddingTop: '60px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
            Les Bulletins Mensuels des Prix
          </Typography>
          <Divider style={{ margin: '20px 0' }} />
          <Typography variant="body1" align="left" gutterBottom>
            Cette section vous offre un accès direct aux analyses mensuelles des prix. Découvrez des rapports complets qui présentent les indicateurs clés et les dernières tendances des prix.
          </Typography>

          {/* Filtre par année */}
          <FormControl fullWidth margin="normal" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Sélectionnez une année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionnez une année de publication"
              variant="outlined"
            >
              <MenuItem value="Tous">Tous les bulletins</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredBulletins.slice(0, visibleCount).map((bulletin, index) => (
              <Grid item xs={12} sm={6} md={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <CardMedia
                      component="img"
                      sx={{ width: 71, height: 90, m: 2, cursor: 'pointer' }}
                      image={bulletin.image}
                      alt={bulletin.title}
                      onClick={() => window.open(bulletin.link, '_blank')}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                        <a
                          href={bulletin.link}
                          style={{ textDecoration: 'none', color: 'inherit' }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <DownloadIcon /> {bulletin.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {bulletin.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié le {moment(bulletin.datePublication).format('DD/MM/YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Bouton "Plus de bulletins" */}
          {visibleCount < filteredBulletins.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Bulletins
            </Button>
          )}
          <Typography variant="caption" color="textSecondary">
            <br />
            <br />
          </Typography>
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            <strong>Notes :</strong> Les Bulletins Mensuels des Prix, qui fournissent des analyses essentielles sur l'évolution des prix, sont publiés chaque mois le <strong>15</strong>, soit 15 jours après la fin du mois précédent.
            Voici les dates de publication prévues pour les mois à venir :
            <ul style={{ listStyleType: 'circle', margin: '10px 0 10px 20px', padding: 0 }}>
              <li><strong>15 janvier</strong> - Publication du bulletin pour le mois de décembre</li>
              <li><strong>15 février</strong> - Publication du bulletin pour le mois de janvier</li>
              <li><strong>15 mars</strong> - Publication du bulletin pour le mois de février</li>
              <li><strong>15 avril</strong> - Publication du bulletin pour le mois de mars</li>
              <li><strong>15 mai</strong> - Publication du bulletin pour le mois d'avril</li>
              <li><strong>15 juin</strong> - Publication du bulletin pour le mois de mai</li>
              <li><strong>15 juillet</strong> - Publication du bulletin pour le mois de juin</li>
              <li><strong>15 août</strong> - Publication du bulletin pour le mois de juillet</li>
              <li><strong>15 septembre</strong> - Publication du bulletin pour le mois d'août</li>
              <li><strong>15 octobre</strong> - Publication du bulletin pour le mois de septembre</li>
              <li><strong>15 novembre</strong> - Publication du bulletin pour le mois d'octobre</li>
              <li><strong>15 décembre</strong> - Publication du bulletin pour le mois de novembre</li>
            </ul>
          </Typography>
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
              onClick={() => navigate('/bulletin')}
              style={{
                marginTop: '5px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page des bulletins
            </Button>
          </Box>
        </Container>
        <Typography variant="caption" color="textSecondary">
          <br />
          <br />
        </Typography>
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default BulletinsPrix;